import PropTypes from 'prop-types';
import React from 'react';

import i18n from '../../i18n';
import Api from '../../api/Api';
import { FLAG_CLEANING_FEES, isFeatureFlagEnabled } from '../../utils/featureFlags';
import Utils from '../../utils/utils';
import { CurrencyFormatter } from '../../utils/Intl';
import Analytics from '../../utils/analytics';
import Conversation from '../../models/Conversation';
import Product from '../../models/Product';
import User from '../../models/User';

import StarRating from '../rating/StarRating';
import ExchangeResume from './ExchangeResume';
import ExchangeRatingOptimalModal from './ExchangeRatingOptimalModal';
import { AlertDialog, Button, Callout, Flex, Text, Title } from '@homeexchange/design';
import useGTM from '../analytics/useGTM';
import { NAVIGATION_ACTION, NAVIGATION_AREAS, NAVIGATION_TEXT } from '../analytics/analytics-constants';

const DO_NOT_ADD_COMMENT = 'DO_NOT_ADD_COMMENT';

export const ratedHomeIds = (conversation) =>
    [conversation.getExchangeAsGuest(), conversation.getExchangeAsHost()]
        .filter((exchange) => exchange && exchange.hasToRate())
        .map((exchange) => exchange.get('home')?.id)
        .filter(Boolean);

export const hasDeclaredProfessionalCleaner = (homes) =>
    homes.some((home) => home?.has_professional_cleaner === true);

export default class ExchangeRating extends React.Component {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        currency: PropTypes.string.isRequired,
        products: PropTypes.arrayOf(PropTypes.instanceOf(Product)),
        user: PropTypes.instanceOf(User).isRequired,
        fetchConversation: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            showOptimalModal: false,
            showRateModal: false,
            ratingCommunication: 0,
            ratingExpectation: 0,
            ratingClean: 0,
            sendIsLoading: false,
            showCleaningHint: false
        };

        this.formRef = React.createRef();
        this.cancelFeedbackRef = React.createRef();
        this.feedbackRef = React.createRef();
        this.expectationRef = React.createRef();
        this.communicationRef = React.createRef();
        this.testimonialRef = React.createRef();
        this.cleanRef = React.createRef();
        this.trustRef = React.createRef();
        this.politeRef = React.createRef();
        this.handleSubmit = this.handleSubmit.bind(this);
        this.closeRatingOptimalModal = this.closeRatingOptimalModal.bind(this);
        this.displayRateModal = this.displayRateModal.bind(this);
    }

    handleSubmit(e) {
        e.preventDefault();
        if (e.target.checkValidity()) {
            this.removeErrorMessages();
            this.sendRating();
        } else {
            this.showErrorMessages();
        }
    }

    displayRateModal() {
        const { user, conversation } = this.props;
        const { showRateModal } = this.state;

        const exchangeAsHost = conversation.getExchangeAsHost();
        const isReciprocal = conversation.getExchange().isReciprocal();
        const shouldDisplayTypeform = isReciprocal || !exchangeAsHost;

        const handleOnClickSurvey = () => {
            const { fetchNavigation } = useGTM(user);

            Analytics.trackGTM(
                'button',
                fetchNavigation(
                    NAVIGATION_ACTION.CLICK,
                    NAVIGATION_AREAS.EXCHANGE_RATE_MODAL,
                    NAVIGATION_TEXT.COMPLETE_THE_SURVEY
                )
            );

            window.open(
                i18n.t('rating:transportation-survey-link', {
                    email: user.get('email'),
                    conversationId: conversation.get('id'),
                    userId: user.get('id')
                }),
                '_blank',
                'noopener, noreferrer'
            );

            location.href = i18n.t('common:reward.url.4');
        };

        return (
            <AlertDialog
                title={i18n.t('rating:modal_reviewExchange_reviewSubmitted_title')}
                isOpen={showRateModal}
                confirmActionText={i18n.t('global:global_returnSearch')}
                confirmActionHierarchy="secondary"
                iconStatus="success"
                onConfirmAction={() => {
                    location.href = i18n.t('common:reward.url.4');
                }}
            >
                <Flex direction="column" gap="8" flexWrap="nowrap">
                    <Text>{i18n.t('rating:modal_reviewExchange_reviewSubmitted_body')}</Text>
                    {shouldDisplayTypeform && (
                        <Callout status="info">
                            <Title level={5}>
                                {i18n.t('rating:modal_reviewExchange_surveyComplete_calloutTitle')}
                            </Title>
                            <Text>{i18n.t('rating:modal_reviewExchange_surveyComplete_calloutBody')}</Text>
                            <Button hierarchy="secondary" onClick={handleOnClickSurvey}>
                                {i18n.t('global:global_completeSurvey')}
                            </Button>
                        </Callout>
                    )}
                </Flex>
            </AlertDialog>
        );
    }

    getCancelFeedback() {
        const data = { feedback: '' };
        const feedbackChecked = this.cancelFeedbackRef.current.querySelector('[name="feedback"]:checked');
        if (feedbackChecked.dataset.feedback !== DO_NOT_ADD_COMMENT) {
            data.feedback = feedbackChecked.value;
        }

        return data;
    }

    sendRating() {
        this.setState({ sendIsLoading: true });

        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        const isMutual = this.props.conversation.isMutual();
        let exchangeCancelled = false;

        const requests = [];
        // canceled exchange rating
        if (exchangeAsGuest && exchangeAsGuest.isCanceled()) {
            const canceledExchangeRation = Api.Conversation.rateHome(
                this.props.conversation.id,
                this.getCancelFeedback()
            );
            requests.push(canceledExchangeRation);
            exchangeCancelled = true;
        } else if (exchangeAsHost && exchangeAsHost.isCanceled()) {
            const canceledExchangeRation = Api.Conversation.rateUser(
                this.props.conversation.id,
                this.getCancelFeedback()
            );
            requests.push(canceledExchangeRation);
            exchangeCancelled = true;
        } else {
            // rates exchange as guest if exists
            if (exchangeAsGuest && exchangeAsGuest.hasToRate()) {
                const homeRatingRequest = Api.Conversation.rateHome(this.props.conversation.id, {
                    communication: this.communicationRef?.current.getValue(),
                    expectation: this.expectationRef?.current.getValue(),
                    clean: this.cleanRef?.current.getValue(),
                    testimonial: this.testimonialRef?.current.value,
                    feedback: this.feedbackRef?.current.value
                });
                requests.push(homeRatingRequest);
            }
            // rates exchange as host if exists
            if (exchangeAsHost && exchangeAsHost.hasToRate()) {
                const userRatingRequest = Api.Conversation.rateUser(this.props.conversation.id, {
                    trust: this.trustRef?.current.getValue(),
                    polite: this.politeRef?.current.getValue(),
                    clean: this.cleanRef?.current.getValue(),
                    testimonial: this.testimonialRef?.current.value,
                    feedback: this.feedbackRef?.current.value
                });
                requests.push(userRatingRequest);
            }
        }
        // dismiss card and update conversation/exchanges on done
        Promise.all(requests).then(
            () => {
                // success
                Analytics.trackGTM('exchange_rated', {
                    event_data: {
                        exchange_type: this.props.conversation.isMutual()
                            ? 'reciprocal_exchange'
                            : 'guest_point_exchange',
                        exchange_role: isMutual ? 'both' : exchangeAsGuest ? 'guest' : 'host',
                        collection: this.props.user.isCollection()
                    }
                });

                this.setState({ showRateModal: true });

                if (window.hj && !exchangeCancelled) {
                    window.hj.locationListener?.setMode('manual'); // Disable issue with URL change
                    const locale = i18n.language || 'en';
                    window.hj('trigger', `exchange-rating-${locale}`);
                }

                this.setState({ sendIsLoading: false });
                this.props.fetchConversation(this.props.conversation.id);
            },
            (error) => {
                // failed
                sweetAlert({
                    icon: 'error',
                    title: i18n.t('common:an_error_occured'),
                    text: i18n.t(`rating:${error.responseJSON.errors.code}`)
                });
                this.setState({ sendIsLoading: false });
            }
        );
    }

    showErrorMessages() {
        Array.prototype.slice
            .call(this.formRef.current.elements)
            .filter((input) => input.checkValidity && !input.checkValidity())
            .forEach((input) => {
                const message = input.validationMessage;
                const formGroup = $(input).closest('.form-group');
                formGroup.addClass('has-error');
                formGroup.find('.help-block').remove();
                formGroup.append(`<div class="help-block">${message}</div>`);
            });
    }

    removeErrorMessages() {
        Array.prototype.slice
            .call(this.formRef.current.elements)
            .filter((input) => !input.checkValidity || input.checkValidity())
            .forEach((input) => {
                const formGroup = $(input).closest('.form-group');
                formGroup.removeClass('has-error');
                formGroup.find('.help-block').remove();
            });
    }

    back(e) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('dismiss_card'));
    }

    getSubscriptionPrice() {
        const { products } = this.props;
        let price = null;
        products.some((product) => {
            if (product.isSubscription()) {
                price = product.get('price');
                return true;
            }
            return false;
        });

        return price;
    }

    closeRatingOptimalModal() {
        this.setState({
            showOptimalModal: false
        });
        document.dispatchEvent(new CustomEvent('dismiss_card'));
    }

    setRating(key, rating) {
        this.setState({ [key]: rating });
    }

    render() {
        const exchange = this.props.conversation.getExchange();

        // should not be abble to rate if exchange is canceled by current user
        if (
            exchange.isCanceled() &&
            exchange.has('user_annulation') &&
            exchange.get('user_annulation').id == this.props.user.id
        ) {
            return (
                <div className="page-404">
                    <p className="error-message">{i18n.t('common:problem_occured')}</p>
                </div>
            );
        }

        return (
            <>
                {this.displayRateModal()}
                <form
                    className="exchange-rating with-sidebar"
                    ref={this.formRef}
                    onSubmit={this.handleSubmit}
                >
                    {(() => {
                        if (exchange.isCanceled()) {
                            return this.renderRatingForCanceledExchange();
                        } else {
                            return this.renderRating();
                        }
                    })()}
                    <div className="sidebar">
                        <ExchangeResume
                            conversation={this.props.conversation}
                            currency={this.props.currency}
                            exchange={this.props.conversation.getExchange()}
                            user={this.props.user}
                        />
                        <Flex direction="row" gap="2" className="exchange-rating-buttons" shrink={false}>
                            <Button hierarchy="secondary" isFullWidth type="button" onClick={this.back}>
                                {i18n.t('messaging:back_to_conversation')}
                            </Button>
                            <Button
                                isLoading={this.state.sendIsLoading}
                                isDisabled={this.state.sendIsLoading}
                                type="submit"
                                isFullWidth
                            >
                                {i18n.t('rating:send_rating')}
                            </Button>
                        </Flex>
                    </div>
                </form>
            </>
        );
    }

    componentDidMount() {
        // The conversation payload (served by core) does not carry the home's professional cleaning
        // declaration, so the rated homes are read from the home API.
        const homeIds = ratedHomeIds(this.props.conversation);
        if (homeIds.length === 0) {
            return;
        }
        Promise.all([
            isFeatureFlagEnabled(FLAG_CLEANING_FEES),
            ...homeIds.map((homeId) => Api.Home.getOne(homeId).catch(() => null))
        ]).then(([isCleaningFeesEnabled, ...homes]) => {
            if (!this.isUnmounted && isCleaningFeesEnabled && hasDeclaredProfessionalCleaner(homes)) {
                this.setState({ showCleaningHint: true });
            }
        });
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    renderRating() {
        const { showOptimalModal } = this.state;
        const optimalPrice = this.getSubscriptionPrice();
        const optimalPriceFormatted = optimalPrice
            ? CurrencyFormatter.format(optimalPrice, this.props.currency)
            : null;
        return (
            <div className="body">
                {this.renderGuestRating()}
                {this.renderHostRating()}
                <ExchangeRatingOptimalModal
                    show={showOptimalModal}
                    optimalPrice={optimalPriceFormatted}
                    closeModal={this.closeRatingOptimalModal}
                />
                <div className="row">
                    <div className="col-sm-7">
                        <div className="form-group">
                            <label htmlFor="feedback">
                                {i18n.t('rating:feedback_label')}
                                <span className="mandatory-field-indicator">*</span>
                            </label>
                            <textarea
                                name="feedback"
                                rows="4"
                                className="form-control"
                                required
                                ref={this.feedbackRef}
                            ></textarea>
                            {this.state.showCleaningHint && (
                                <Text size="sm" color="secondary">
                                    {i18n.t('home:page_rating_cleaningfees_hint')}
                                </Text>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="testimonial">{i18n.t('rating:testimonial_label')}</label>
                            <textarea
                                name="testimonial"
                                rows="4"
                                className="form-control"
                                ref={this.testimonialRef}
                            ></textarea>
                        </div>
                    </div>
                    <div className="col-sm-5">
                        <ul className="advices">
                            {/* <li>{i18n.t('rating:advice_1', { first_name: this.props.conversation.get('interlocutor').get('first_name') })}</li> */}
                            <li>{i18n.t('rating:advice_2')}</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    renderGuestRating() {
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        if (exchangeAsGuest && exchangeAsGuest.hasToRate()) {
            return (
                <article className="guest-rating">
                    <div className="header">
                        <h1>
                            {i18n.t('rating:guest_rating_title', {
                                first_name: exchangeAsGuest.get('host').get('first_name'),
                                location: exchangeAsGuest.get('home').location()
                            })}
                        </h1>
                    </div>
                    <p
                        className="rating-rule-explanation"
                        dangerouslySetInnerHTML={{
                            __html: Utils.nl2br(i18n.t('rating:rule-explanation'))
                        }}
                    ></p>
                    <div className="row">
                        {['communication', 'expectation', 'clean'].map((ratingKey) => {
                            let ref;
                            switch (ratingKey) {
                                case 'communication':
                                    ref = this.communicationRef;
                                    break;
                                case 'expectation':
                                    ref = this.expectationRef;
                                    break;
                                case 'clean':
                                    ref = this.cleanRef;
                                    break;
                            }
                            return ref ? (
                                <div key={ratingKey} className="col-sm-4">
                                    <div className="form-group">
                                        <label>{i18n.t(`rating:${ratingKey}`)}</label>
                                        <div>
                                            <StarRating
                                                name={ratingKey}
                                                ref={ref}
                                                rating={this.state[ratingKey]}
                                                setRating={this.setRating.bind(this, ratingKey)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : null;
                        })}
                    </div>
                </article>
            );
        }
        return false;
    }

    renderHostRating() {
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        if (exchangeAsHost && exchangeAsHost.hasToRate()) {
            return (
                <article className="host-rating">
                    <div className="header">
                        <h1>
                            {i18n.t('rating:host_rating_title', {
                                first_name: exchangeAsHost.get('guest').get('first_name'),
                                location: exchangeAsHost.get('home').location()
                            })}
                        </h1>
                    </div>
                    {!this.props.conversation.isMutual() && (
                        <p
                            className="rating-rule-explanation"
                            dangerouslySetInnerHTML={{
                                __html: Utils.nl2br(i18n.t('rating:rule-explanation'))
                            }}
                        ></p>
                    )}
                    <div className="row">
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label>{i18n.t('rating:politeness')}</label>
                                <div>
                                    <StarRating name="polite" ref={this.politeRef} />
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label>{i18n.t('rating:trustworthiness')}</label>
                                <div>
                                    <StarRating name="trust" ref={this.trustRef} />
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-4">
                            <div className="form-group">
                                <label>{i18n.t('rating:cleanliness')}</label>
                                <div>
                                    <StarRating name="clean" ref={this.cleanRef} />
                                </div>
                            </div>
                        </div>
                    </div>
                </article>
            );
        }
        return false;
    }

    renderRatingForCanceledExchange() {
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        const context = exchangeAsGuest ? 'host' : 'guest';
        return (
            <div className="body">
                <div className="header">
                    <h1>
                        {(() => {
                            if (exchangeAsGuest) {
                                return i18n.t('rating:canceled_by_host_rating_title', {
                                    first_name: exchangeAsGuest.get('host').get('first_name'),
                                    location: exchangeAsGuest.get('home').location()
                                });
                            } else {
                                return i18n.t('rating:canceled_by_guest_rating_title', {
                                    first_name: exchangeAsHost.get('guest').get('first_name'),
                                    location: exchangeAsHost.get('home').location()
                                });
                            }
                        })()}
                    </h1>
                </div>
                <div className="form-group" id="cancel_feedback" ref={this.cancelFeedbackRef}>
                    <div className="radio">
                        <label htmlFor="cancel_feedback_1">
                            <input
                                type="radio"
                                name="feedback"
                                id="cancel_feedback_1"
                                value={i18n.t('rating:cancel_feedback_1', { context })}
                                required
                            />
                            {i18n.t('rating:cancel_feedback_1', { context })}
                        </label>
                    </div>
                    <div className="radio">
                        <label htmlFor="cancel_feedback_2">
                            <input
                                data-feedback={DO_NOT_ADD_COMMENT}
                                type="radio"
                                name="feedback"
                                id="cancel_feedback_2"
                                value={i18n.t('rating:cancel_feedback_2', { context })}
                                required
                            />
                            {i18n.t('rating:cancel_feedback_2', { context })}
                        </label>
                    </div>
                    <div className="radio">
                        <label htmlFor="cancel_feedback_3">
                            <input
                                type="radio"
                                name="feedback"
                                id="cancel_feedback_3"
                                value={i18n.t('rating:cancel_feedback_3', { context })}
                                required
                            />
                            {i18n.t('rating:cancel_feedback_3', { context })}
                        </label>
                    </div>
                </div>
            </div>
        );
    }
}
